import React, { useState, useEffect } from 'react';
import { TActions, TState, PaymentType } from '@frontend/applications/PaymentsApp/hooks/usePayments/state';
import {
 ColumnsType, Input, Table, Typography, Radio,
} from '@revfluence/fresh';
import { TSTAPayment } from '@frontend/applications/PaymentsApp/types';
import { formatValue, DataFormat } from '@affiliates/utils';
import { StepDescription } from '@frontend/applications/PaymentsApp/components/PaymentsWizard/common/StepDescription';
import { SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import NumericInput from '@frontend/applications/PaymentsApp/components/PaymentsWizard/common/NumericInput/NumericInput';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import styles from './NewBulkPaymentsTable.scss';

const { Title } = Typography;

interface IProps {
  actions: TActions;
  state: TState;
}

export const NewBulkPaymentsTable: React.FC<IProps> = (props) => {
  const { actions, state } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const { cashbackPayments: isCashbackPaymentsEnabled } = useClientFeatures();
  console.log({
    staPayments: state.staPayments,
    selectedPaymentType: state.selectedPaymentType,
  });

  useEffect(() => {
    actions.setAmountPaying(
      state.staPayments.filter((payment) => payment.isSelected).reduce((sum, payment) => parseFloat(payment.amountToPay) + sum, 0),
    );
  }, [state.staPayments, actions]);

  const onSelectChange = (selectedRowKeys: React.Key[]) => {
    const updatedPayments = state.staPayments.map((payment) => ({
      ...payment,
      isSelected: selectedRowKeys.includes(payment.key),
    }));
    actions.setSTAPayments(updatedPayments);
  };

  const rowSelection = {
    onChange: onSelectChange,
    getCheckboxProps: (record: TSTAPayment) => ({
      disabled: record.isMemberDeleted || !record.paypalEmail,
    }),
  };

  const onPaymentChange = (newValue:string, record: TSTAPayment) => {
    const updatedPayments = state.staPayments.map((payment) => {
      if (payment.key === record.key) {
        return {
          ...payment,
          amountToPay: newValue,
        };
      } else {
        return payment;
      }
    });
    actions.setSTAPayments(updatedPayments);
  };
  const getColumns = (): ColumnsType<TSTAPayment> => {
    const baseColumns: ColumnsType<TSTAPayment> = [
      {
        title: 'Member',
        dataIndex: 'member',
        width: 100,
        className: styles.ellipsis,
      },
      {
        title: 'Offer',
        dataIndex: 'offerName',
        width: 100,
      },
      {
        title: 'Last Payout',
        dataIndex: 'lastPayout',
        width: 100,
        render: (lastPayout: string) => `$${formatValue(DataFormat.Money, lastPayout)}`,
      },
    ];

    if (isCashbackPaymentsEnabled && state.selectedPaymentType === PaymentType.FULL_PAYMENT) {
      // Full Payment: Show Total Due, Commission, Rewards columns
      baseColumns.push(
        {
          title: 'Total Due',
          dataIndex: 'totalDue',
          width: 100,
          render: (totalDue: number, record: TSTAPayment) => {
            const amount = record.isGroupedRow ? (totalDue || record.amountDue) : record.amountDue;
            return `$${formatValue(DataFormat.Money, amount)}`;
          },
        },
        {
          title: 'Commission',
          dataIndex: 'commissionAmount',
          width: 100,
          render: (commissionAmount: number, record: TSTAPayment) => {
            if (record.isGroupedRow) {
              return `$${formatValue(DataFormat.Money, commissionAmount || 0)}`;
            }
            return `$${formatValue(DataFormat.Money, 0)}`;
          },
        },
        {
          title: 'Rewards',
          dataIndex: 'rewardsAmount',
          width: 100,
          render: (rewardsAmount: number, record: TSTAPayment) => {
            if (record.isGroupedRow) {
              return `$${formatValue(DataFormat.Money, rewardsAmount || 0)}`;
            }
            return `$${formatValue(DataFormat.Money, 0)}`;
          },
        }
      );
    } else if (isCashbackPaymentsEnabled && state.selectedPaymentType === PaymentType.CASHBACK_ONLY) {
      // Cashback Only: Show non-editable Rewards column
      baseColumns.push({
        title: 'Rewards Due',
        dataIndex: 'amountDue',
        width: 100,
        render: (amountDue: number) => `$${formatValue(DataFormat.Money, amountDue)}`,
      });
    } else {
      // Commission Only or default: Show existing Amount Due and editable Amount to Pay
      baseColumns.push(
        {
          title: 'Amount Due',
          dataIndex: 'amountDue',
          width: 100,
          render: (amountDue: number) => `$${formatValue(DataFormat.Money, amountDue)}`,
        },
        {
          title: 'Amount to Pay',
          dataIndex: 'amountToPay',
          width: 100,
          render: (amountToPay: string, record: TSTAPayment) => (
            <NumericInput
              prefix="$"
              value={amountToPay}
              disabled={!record.paypalEmail || !state.featureFlags.migrateToGraphQL}
              onChange={(newValue) => onPaymentChange(newValue, record)}
              max={9999.99}
            />
          ),
        }
      );
    }

    // Always add PayPal Email column at the end
    baseColumns.push({
      title: 'PayPal Email',
      dataIndex: 'paypalEmail',
      width: 100,
      render: (paypalEmail: string, record: TSTAPayment) => {
        if (record.isMemberDeleted) {
          return <span style={{ color: 'red' }}>Member is deleted</span>;
        }
        return paypalEmail || <span style={{ color: 'red' }}>PayPal email required</span>;
      },
    });

    return baseColumns;
  };

  const filteredPayments = state.staPayments.filter((payment) =>
    payment.member.toLowerCase().includes(searchQuery.toLowerCase()));

  const rowClassName = (record: TSTAPayment) =>
    (record.isMemberDeleted || !record.paypalEmail ? styles.disabledRow : '');

  // Calculate footer totals for selected payments
  const selectedPayments = state.staPayments.filter((payment) => payment.isSelected);
  const selectedCount = selectedPayments.length;

  const calculateTotals = () => {
    if (!isCashbackPaymentsEnabled || state.selectedPaymentType === PaymentType.COMMISSION_ONLY) {
      // Commission Only: Sum amountToPay
      const total = selectedPayments.reduce((sum, payment) => sum + parseFloat(payment.amountToPay), 0);
      return { total, commissions: 0, rewards: 0 };
    }

    if (state.selectedPaymentType === PaymentType.CASHBACK_ONLY) {
      // Cashback Only: Sum amountDue (rewards)
      const rewards = selectedPayments.reduce((sum, payment) => sum + payment.amountDue, 0);
      return { total: rewards, commissions: 0, rewards };
    }

    if (state.selectedPaymentType === PaymentType.FULL_PAYMENT) {
      // Full Payment: Sum commissions and rewards separately
      const commissions = selectedPayments.reduce((sum, payment) => sum + (payment.commissionAmount || 0), 0);
      const rewards = selectedPayments.reduce((sum, payment) => sum + (payment.rewardsAmount || 0), 0);
      const total = commissions + rewards;
      return { total, commissions, rewards };
    }

    return { total: 0, commissions: 0, rewards: 0 };
  };

  const { total, commissions, rewards } = calculateTotals();

  const getFooterText = () => {
    if (!isCashbackPaymentsEnabled || state.selectedPaymentType === PaymentType.COMMISSION_ONLY) {
      return `Total payout: $${formatValue(DataFormat.Money, total)}`;
    }

    if (state.selectedPaymentType === PaymentType.CASHBACK_ONLY) {
      return `Total reward payment: $${formatValue(DataFormat.Money, total)}`;
    }

    if (state.selectedPaymentType === PaymentType.FULL_PAYMENT) {
      return `Total payout: $${formatValue(DataFormat.Money, total)} ($${formatValue(DataFormat.Money, commissions)} Commissions + $${formatValue(DataFormat.Money, rewards)} Rewards)`;
    }

    return '';
  };

  return (
    <div className={styles.newBulkPaymentsTableContainer}>
      <StepDescription
        title="Confirm Payment"
        description={!isCashbackPaymentsEnabled ? 'Confirm all of the member information below before continuing to payment details. If necessary, additional funds can be added to your account in Payment Settings.' : 'Review payout details for each selected member. You can process commissions, rewards, or both. Members with missing payment info will be skipped. If necessary, additional funds can be added to your account in Payment Settings.'}
      />
      {isCashbackPaymentsEnabled && (
        <div className={styles.paymentTypeContainer}>
          <Title level={5}>Payment Type</Title>
          <Radio.Group
            value={state.selectedPaymentType}
            onChange={(e) => actions.setSelectedPaymentType(e.target.value)}
            className={styles.paymentTypeRadio}
          >
            <Radio.Button value={PaymentType.FULL_PAYMENT}>Make Full Payment</Radio.Button>
            <Radio.Button value={PaymentType.COMMISSION_ONLY}>Pay for Commission</Radio.Button>
            <Radio.Button value={PaymentType.CASHBACK_ONLY}>Pay for Rewards</Radio.Button>
          </Radio.Group>
        </div>
      )}
      <div className={styles.headerContainer}>
        <div>
          <Title level={5}>
            {`${state.staPayments.length} Members (${state.staPayments.filter((payment) => payment.isSelected).length} Selected)`}
          </Title>
        </div>
        <div>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search Member"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 200 }}
          />
        </div>
      </div>
      <Table
        size="middle"
        dataSource={filteredPayments}
        columns={getColumns()}
        pagination={false}
        rowSelection={rowSelection}
        rowClassName={rowClassName}
        scroll={{ x: 'max-content' }}
      />
      {isCashbackPaymentsEnabled && selectedCount > 0 && (
        <div className={styles.footerContainer}>
          <div className={styles.footerLeft}>
            <CheckCircleOutlined className={styles.checkIcon} />
            <span className={styles.selectedText}>{selectedCount} Members Selected</span>
          </div>
          <div className={styles.footerRight}>
            <span className={styles.totalText}>{getFooterText()}</span>
          </div>
        </div>
      )}
    </div>
  );
};
